import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useSelector } from 'react-redux';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HeatmapModule from 'highcharts/modules/heatmap';
import { Search as SearchIcon } from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { Filters } from './Filters';
import Loading from '../../components/Loading';
import { SegmentedControl } from '../../components/ui';
import ScreenStatePanel from '../../components/Insights/ScreenStatePanel';
import EntitySelectionModal from '../../components/Reports/EntitySelectionModal';
import useRetentionScreenState from '../../hooks/useRetentionScreenState';
import {
  formatNumber,
  getSuggestedPeriodByDataRecency,
  getUniqueTooltipPoints
} from '../../utils';
import { sendRequest } from '../../utils/dateHelpers';
import { getValidDevFeedCursor } from '../../utils/devFeedCursor';
import { sendAnalyticsSessionLog } from '../../utils/analytics';
import {
  getAverageDeveloperCostFromSource,
  parseNumericInput
} from '../../utils/companyConfigurations';
import {
  getCohortCellTheme,
  getHeatmapDataLabelColor,
  getRetentionHeatmapStops
} from '../../utils/heatmapTheme';
import {
  getRetentionScreenOverlayContent,
  RETENTION_SCREEN_STATES
} from '../../constants/retentionScreenState';
import { findOptionByValue } from '../../utils/reportData';

HeatmapModule(Highcharts);

const PERIODS = [
  { label: 'Week', value: 'week' },
  { label: 'Month', value: 'month' },
  { label: 'Year', value: 'year' }
];
const FEED_RENDER_PAGE_SIZE = 30;
const FEED_REQUEST_PAGE_SIZE = 100;
const PIE_COLORS = [
  '#36A2EB',
  '#4C51BF',
  '#15C76F',
  '#FF6D2D',
  '#5C82B8',
  '#C153E8',
  '#EF4444',
  '#0EA5E9'
];

const getProjectUid = projectFilter => {
  if (!projectFilter) return '';
  if (Array.isArray(projectFilter)) return projectFilter[0]?.value || '';
  return projectFilter.value || '';
};

const formatDateForApi = date => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getFixedDateRange = () => {
  const endDate = new Date();
  const startDate = new Date(endDate.getFullYear(), endDate.getMonth() - 12, 1);

  return {
    startDate: formatDateForApi(startDate),
    endDate: formatDateForApi(endDate)
  };
};

const getAverageDeveloperCost = userInfo => {
  const directCost = getAverageDeveloperCostFromSource(userInfo);
  if (directCost > 0) return directCost;
  return getAverageDeveloperCostFromSource(userInfo?.user);
};

const convertEffortToInvestment = (value, averageCost) => {
  const effortValue = parseNumericInput(value);
  return ((Number.isFinite(effortValue) ? effortValue : 0) / 60) * averageCost;
};

const convertNumericValues = (payload, averageCost) => {
  if (Array.isArray(payload)) {
    return payload.map(item => convertNumericValues(item, averageCost));
  }

  if (payload && typeof payload === 'object') {
    return Object.fromEntries(
      Object.entries(payload).map(([key, value]) => [
        key,
        convertNumericValues(value, averageCost)
      ])
    );
  }

  if (typeof payload === 'number' || typeof payload === 'string') {
    const parsedValue = parseNumericInput(payload);

    if (Number.isFinite(parsedValue)) {
      return convertEffortToInvestment(parsedValue, averageCost);
    }
  }

  return payload;
};

const convertTrendResponseToInvestment = (responseData, averageCost) => ({
  ...(responseData || {}),
  groupedDataByPeriod: convertNumericValues(
    responseData?.groupedDataByPeriod,
    averageCost
  ),
  total: convertNumericValues(responseData?.total, averageCost)
});

const createSeriesFromMap = (map = {}) => {
  const dates = Object.keys(map).sort((a, b) => new Date(a) - new Date(b));
  return {
    categories: dates,
    values: dates.map(date => map[date] ?? null)
  };
};

const sumNumericValues = values =>
  (values || []).reduce((sum, value) => {
    if (typeof value !== 'number' || Number.isNaN(value)) return sum;
    return sum + value;
  }, 0);

const hasNumericSeriesData = values =>
  (values || []).some(
    value => typeof value === 'number' && !Number.isNaN(value)
  );

const buildDistributionFromTotals = (totals, labelResolver, maxItems = 10) =>
  Object.entries(totals || {})
    .map(([key, value]) => ({
      name: labelResolver(key),
      y: value
    }))
    .filter(item => Number(item.y) > 0)
    .sort((a, b) => b.y - a.y)
    .slice(0, maxItems);

const buildCategoryDistribution = (trendData, categoriesMap = {}) => {
  const totalsByCategory = {};

  Object.entries(trendData?.groupedDataByPeriod || {}).forEach(([, values]) => {
    Object.entries(values || {}).forEach(([categoryKey, value]) => {
      if (typeof value !== 'number' || Number.isNaN(value)) return;
      totalsByCategory[categoryKey] =
        Number(totalsByCategory[categoryKey] || 0) + value;
    });
  });

  return Object.entries(totalsByCategory)
    .map(([categoryKey, value]) => ({
      name: categoriesMap?.[categoryKey]?.label || categoryKey,
      y: value
    }))
    .filter(item => Number(item.y) > 0)
    .sort((a, b) => b.y - a.y)
    .slice(0, 8);
};

const buildAiDevDistribution = (
  aiTotals = {},
  developerTotals = {},
  isInvestment = false
) =>
  [
    {
      name: isInvestment ? 'Estimated Cost Avoided' : 'Effort Saved by AI',
      y: sumNumericValues(Object.values(aiTotals || {})),
      color: '#8B5CF6'
    },
    {
      name: isInvestment
        ? 'Estimated Human Cost Required'
        : 'Human Effort Required',
      y: sumNumericValues(Object.values(developerTotals || {})),
      color: '#3B82F6'
    }
  ].filter(item => Number(item.y) > 0);

const getTopKeysByTotal = (trendData, maxItems = 8) => {
  const sortedByTotal = Object.entries(trendData?.total || {})
    .filter(([, value]) => typeof value === 'number' && !Number.isNaN(value))
    .sort((a, b) => b[1] - a[1])
    .slice(0, maxItems)
    .map(([key]) => key);

  if (sortedByTotal.length) return sortedByTotal;

  const totalsFromPeriods = {};
  Object.values(trendData?.groupedDataByPeriod || {}).forEach(values => {
    Object.entries(values || {}).forEach(([key, value]) => {
      if (typeof value !== 'number' || Number.isNaN(value)) return;
      totalsFromPeriods[key] = Number(totalsFromPeriods[key] || 0) + value;
    });
  });

  return Object.entries(totalsFromPeriods)
    .sort((a, b) => b[1] - a[1])
    .slice(0, maxItems)
    .map(([key]) => key);
};

const getPieColor = (item, index) =>
  item?.color || PIE_COLORS[index % PIE_COLORS.length];

const formatPiePercentage = value => {
  if (!Number.isFinite(value)) return '0%';
  return `${Number(value.toFixed(1))}%`;
};

const buildZoomableChartOptions = (options, zoomType = 'x') => ({
  ...options,
  chart: {
    ...options?.chart,
    zooming: {
      type: zoomType
    },
    panning: {
      enabled: true,
      type: zoomType === 'xy' ? 'xy' : 'x'
    },
    panKey: 'shift'
  }
});

const renderPieLegend = (items = [], isDarkMode = false) => {
  if (!items.length) return null;

  const total = items.reduce((sum, item) => sum + Number(item?.y || 0), 0);

  return (
    <div className="mt-3 overflow-x-auto">
      <div className="inline-flex min-w-full items-center gap-4 whitespace-nowrap pb-1">
        {items.map((item, index) => {
          const value = Number(item?.y || 0);
          const percent = total > 0 ? (value / total) * 100 : 0;

          return (
            <div
              key={`${item?.name || 'slice'}-${index}`}
              className={`inline-flex items-center gap-2 text-sm ${
                isDarkMode ? 'text-slate-200' : 'text-slate-700'
              }`}
            >
              <span
                className="h-2.5 w-2.5 rounded-full"
                style={{ backgroundColor: getPieColor(item, index) }}
              />
              <span>{item?.name || 'Unknown'}</span>
              <span
                className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}
              >
                ({formatPiePercentage(percent)})
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const formatSharedLineTooltip = function () {
  const isDarkMode =
    typeof document !== 'undefined' && document.body.classList.contains('dark');
  const points = getUniqueTooltipPoints(this.points);
  if (!points.length) return '';

  const chart = points[0]?.series?.chart;
  const hoveredSeriesName =
    chart?.hoverPoint?.series?.name || chart?.hoverSeries?.name || '';

  points.sort((a, b) => {
    const aHovered = a.series.name === hoveredSeriesName;
    const bHovered = b.series.name === hoveredSeriesName;
    if (aHovered && !bHovered) return -1;
    if (!aHovered && bHovered) return 1;
    return 0;
  });

  const title = this.x ?? '';
  const rows = points
    .map((point, index) => {
      const isFocused = hoveredSeriesName
        ? point.series.name === hoveredSeriesName
        : index === 0;
      const pointValue =
        typeof point.y === 'number' && !Number.isNaN(point.y)
          ? formatNumber(point.y)
          : '-';

      return `<div style="display:flex;align-items:center;justify-content:space-between;gap:10px;margin-top:2px;">
        <span style="display:flex;align-items:center;gap:6px;color:${isDarkMode ? '#cbd5e1' : '#334155'};">
          <span style="color:${point.color};font-size:11px;">●</span>
          ${point.series.name}
        </span>
        <span style="${
          isFocused
            ? `font-weight:700;color:${isDarkMode ? '#f8fafc' : '#0f172a'};`
            : `font-weight:500;color:${isDarkMode ? '#94a3b8' : '#475569'};`
        }">${pointValue}</span>
      </div>`;
    })
    .join('');

  return `<div style="min-width:180px;padding:10px 12px;border-radius:12px;background:${isDarkMode ? '#0f172a' : '#ffffff'};border:1px solid ${isDarkMode ? '#334155' : '#e2e8f0'};box-shadow:0 10px 30px rgba(15,23,42,0.18);">
    <div style="font-size:11px;color:${isDarkMode ? '#94a3b8' : '#64748b'};margin-bottom:4px;">${title}</div>
    ${rows}
  </div>`;
};

const formatSharedStackedTooltip = function () {
  const isDarkMode =
    typeof document !== 'undefined' && document.body.classList.contains('dark');
  const points = getUniqueTooltipPoints(this.points);
  if (!points.length) return '';

  const chart = points[0]?.series?.chart;
  const hoveredSeriesName =
    chart?.hoverPoint?.series?.name || chart?.hoverSeries?.name || '';

  points.sort((a, b) => {
    const aHovered = a.series.name === hoveredSeriesName;
    const bHovered = b.series.name === hoveredSeriesName;
    if (aHovered && !bHovered) return -1;
    if (!aHovered && bHovered) return 1;
    return 0;
  });

  const title = this.x ?? '';
  const rows = points
    .map((point, index) => {
      const isFocused = hoveredSeriesName
        ? point.series.name === hoveredSeriesName
        : index === 0;
      const percentageValue = Number(point?.percentage || 0);
      const roundedPercentage = Number(percentageValue.toFixed(1));
      const pointValue =
        typeof point.y === 'number' && !Number.isNaN(point.y)
          ? formatNumber(point.y)
          : '-';

      return `<div style="display:flex;align-items:center;justify-content:space-between;gap:10px;margin-top:2px;">
        <span style="display:flex;align-items:center;gap:6px;color:${isDarkMode ? '#cbd5e1' : '#334155'};">
          <span style="color:${point.color};font-size:11px;">●</span>
          ${point.series.name}
        </span>
        <span style="${
          isFocused
            ? `font-weight:700;color:${isDarkMode ? '#f8fafc' : '#0f172a'};`
            : `font-weight:500;color:${isDarkMode ? '#94a3b8' : '#475569'};`
        }">${roundedPercentage}% <span style="font-weight:500;color:${isDarkMode ? '#94a3b8' : '#64748b'};">(${pointValue})</span></span>
      </div>`;
    })
    .join('');

  return `<div style="min-width:190px;padding:10px 12px;border-radius:12px;background:${isDarkMode ? '#0f172a' : '#ffffff'};border:1px solid ${isDarkMode ? '#334155' : '#e2e8f0'};box-shadow:0 10px 30px rgba(15,23,42,0.18);">
    <div style="font-size:11px;color:${isDarkMode ? '#94a3b8' : '#64748b'};margin-bottom:4px;">${title}</div>
    ${rows}
  </div>`;
};

const createHeatmapData = (
  retention,
  metricKey = 'start_value_ratio',
  { displayMode = 'git_effort', averageDeveloperCost = 0 } = {}
) => {
  const rawRetention = Array.isArray(retention) ? retention : [];
  const baseXCategories = [...new Set(rawRetention.map(point => point.x))].sort(
    (a, b) => a - b
  );
  const yCategories = [...new Set(rawRetention.map(point => point.y))].sort();
  const xOrderMap = baseXCategories.reduce((acc, category, index) => {
    acc[String(category)] = index;
    return acc;
  }, {});
  const cohortStartByCategory = rawRetention.reduce((acc, point) => {
    const yCategory = point?.y;
    const pointXOrder = xOrderMap[String(point?.x)];
    const existingPoint = acc[yCategory];
    const existingXOrder = xOrderMap[String(existingPoint?.x)];

    if (
      !existingPoint ||
      (Number.isInteger(pointXOrder) &&
        (!Number.isInteger(existingXOrder) || pointXOrder < existingXOrder))
    ) {
      acc[yCategory] = point;
    }

    return acc;
  }, {});

  const isInvestment = displayMode === 'investment';
  const firstColumnLabel = isInvestment
    ? 'Estimated Cost Without AI ($)'
    : 'REV';
  const xCategories = [firstColumnLabel, ...baseXCategories];
  const cohortValues = yCategories.map((category, index) => {
    const isDarkMode =
      typeof document !== 'undefined' &&
      document.body.classList.contains('dark');
    const cohortCellTheme = getCohortCellTheme(isDarkMode);
    const cohortStartPoint = cohortStartByCategory?.[category];
    const revValue = parseNumericInput(cohortStartPoint?.rev);
    const normalizedRev = Number.isFinite(revValue) ? revValue : 0;
    const value = isInvestment
      ? Math.round((normalizedRev / 60) * averageDeveloperCost)
      : Math.round(normalizedRev);

    return {
      x: 0,
      y: index,
      value,
      isCohortValue: true,
      color: cohortCellTheme.fill,
      borderColor: cohortCellTheme.border
    };
  });

  const retentionValues = rawRetention.map(point => ({
    x: baseXCategories.indexOf(point.x) + 1,
    y: yCategories.indexOf(point.y),
    value: Math.round(
      point?.[metricKey] ?? point.start_value_ratio ?? point.start_value ?? 0
    ),
    isCohortValue: false
  }));

  return {
    data: [...cohortValues, ...retentionValues],
    xCategories,
    yCategories,
    isInvestment
  };
};

const ProjectReport = () => {
  const { userInfo = {} } = useSelector(state => state);
  const { screenState: baseRetentionScreenState, screenStateMeta } =
    useRetentionScreenState();
  const fixedDateRange = useMemo(() => getFixedDateRange(), []);
  const [filters, setFilters] = useState(() => ({
    dateRange: fixedDateRange,
    operation: 'git_effort',
    calculationsMethod: 'ratio'
  }));
  const [activePeriod, setActivePeriod] = useState('month');
  const [repoTrendViewMode, setRepoTrendViewMode] = useState('line');
  const [categoryTrendViewMode, setCategoryTrendViewMode] = useState('line');
  const [isDarkMode, setIsDarkMode] = useState(() =>
    typeof document !== 'undefined'
      ? document.body.classList.contains('dark')
      : false
  );

  const [loading, setLoading] = useState(false);
  const [summaryTotals, setSummaryTotals] = useState({
    effort: null,
    investment: null
  });
  const [projectTrendChart, setProjectTrendChart] = useState({
    categories: [],
    series: []
  });
  const [repoTrendChart, setRepoTrendChart] = useState({
    categories: [],
    series: []
  });
  const [categoryTrendChart, setCategoryTrendChart] = useState({
    categories: [],
    series: []
  });
  const [developerDistribution, setDeveloperDistribution] = useState([]);
  const [aiDistribution, setAiDistribution] = useState([]);
  const [repoDistribution, setRepoDistribution] = useState([]);
  const [categoryDistribution, setCategoryDistribution] = useState([]);
  const [retention, setRetention] = useState([]);
  const [reportError, setReportError] = useState('');

  const [feedItems, setFeedItems] = useState([]);
  const [feedError, setFeedError] = useState('');
  const [feedCursor, setFeedCursor] = useState(null);
  const [feedHasMore, setFeedHasMore] = useState(true);
  const [feedLoading, setFeedLoading] = useState(false);

  const [rankedProjects, setRankedProjects] = useState([]);
  const [projectSearchQuery, setProjectSearchQuery] = useState('');
  const [isProjectSearchFocused, setIsProjectSearchFocused] = useState(false);
  const [isProjectSelectionModalOpen, setIsProjectSelectionModalOpen] =
    useState(false);
  const [fallbackAverageDeveloperCost, setFallbackAverageDeveloperCost] =
    useState(0);

  const projectSearchRef = useRef(null);
  const projectSearchInputRef = useRef(null);
  const hasUserSelectedProjectRef = useRef(false);
  const periodManualOverrideRef = useRef(false);
  const periodAutoResolvedRef = useRef(false);
  const skipSelectionSyncRef = useRef('');

  const feedRequestIdRef = useRef(0);
  const feedHasMoreRef = useRef(true);
  const feedLoadingRef = useRef(false);
  const feedBufferedItemsRef = useRef([]);
  const feedBackendHasMoreRef = useRef(true);

  const projectsMap = userInfo?.dashboardData?.projects || {};
  const roleBasedAverageDeveloperCost = getAverageDeveloperCost(userInfo);
  const averageDeveloperCost =
    roleBasedAverageDeveloperCost > 0
      ? roleBasedAverageDeveloperCost
      : fallbackAverageDeveloperCost;

  const projectsList = useMemo(
    () =>
      Object.values(projectsMap).sort((a, b) =>
        a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })
      ),
    [projectsMap]
  );

  const selectedProjectUid = getProjectUid(filters?.projects);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;

    const updateTheme = () => {
      setIsDarkMode(document.body.classList.contains('dark'));
    };

    updateTheme();

    const observer = new MutationObserver(updateTheme);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['class']
    });

    return () => observer.disconnect();
  }, []);
  const selectedProject = selectedProjectUid
    ? projectsMap?.[selectedProjectUid]
    : null;

  const logProjectReportInteraction = useCallback(
    (action, extraPayload = {}) => {
      sendAnalyticsSessionLog({
        eventName: 'project_report_interaction',
        payload: {
          action,
          path: window.location.pathname,
          selected_project_uid: selectedProjectUid || null,
          selected_project_label: selectedProject?.label || null,
          period: activePeriod,
          ...extraPayload
        }
      });
    },
    [activePeriod, selectedProject?.label, selectedProjectUid]
  );

  useEffect(() => {
    setRankedProjects(projectsList);
  }, [projectsList]);

  useEffect(() => {
    if (!projectsList.length || selectedProjectUid) return;
    setIsProjectSelectionModalOpen(true);
  }, [projectsList.length, selectedProjectUid]);

  useEffect(() => {
    if (!selectedProjectUid) return;
    setIsProjectSelectionModalOpen(false);
  }, [selectedProjectUid]);

  useEffect(() => {
    periodManualOverrideRef.current = false;
    periodAutoResolvedRef.current = false;
  }, [
    selectedProjectUid,
    filters?.dateRange?.startDate,
    filters?.dateRange?.endDate
  ]);

  const handleFilters = useCallback(
    nextFilters => {
      const normalizedProject = Array.isArray(nextFilters?.projects)
        ? nextFilters.projects[0]
        : nextFilters?.projects;
      const nextProject =
        typeof normalizedProject === 'undefined'
          ? filters?.projects
          : normalizedProject;

      setFilters({
        ...filters,
        ...nextFilters,
        projects: nextProject
      });
    },
    [filters]
  );

  const navigateProject = useCallback(
    direction => {
      if (!rankedProjects.length || !selectedProjectUid) return;

      const currentIndex = rankedProjects.findIndex(
        item => item.value === selectedProjectUid
      );
      if (currentIndex === -1) return;

      const targetIndex =
        direction === 'next'
          ? Math.min(currentIndex + 1, rankedProjects.length - 1)
          : Math.max(currentIndex - 1, 0);
      const targetProject = rankedProjects[targetIndex];
      const isChanged = targetIndex !== currentIndex;

      logProjectReportInteraction('project_navigation_click', {
        direction,
        from_project_uid: selectedProjectUid,
        to_project_uid: targetProject?.value || null,
        to_project_label: targetProject?.label || null,
        changed: isChanged
      });

      if (!isChanged) return;

      hasUserSelectedProjectRef.current = true;
      setFilters(prev => ({
        ...prev,
        projects: rankedProjects[targetIndex]
      }));
    },
    [logProjectReportInteraction, rankedProjects, selectedProjectUid]
  );

  const handlePeriodChange = useCallback(
    nextPeriod => {
      logProjectReportInteraction('period_toggle_click', {
        next_period: nextPeriod
      });

      if (nextPeriod === activePeriod) return;
      periodManualOverrideRef.current = true;
      periodAutoResolvedRef.current = true;
      setActivePeriod(nextPeriod);
    },
    [activePeriod, logProjectReportInteraction]
  );

  const projectSearchOptions = useMemo(
    () => (rankedProjects.length ? rankedProjects : projectsList),
    [projectsList, rankedProjects]
  );

  const selectedProjectOption = useMemo(() => {
    return (
      projectSearchOptions.find(
        option => option.value === selectedProjectUid
      ) || null
    );
  }, [projectSearchOptions, selectedProjectUid]);

  const handleProjectSearchSelect = useCallback(
    option => {
      if (!option?.value || option.value === selectedProjectUid) return;

      logProjectReportInteraction('project_search_select', {
        selected_project_uid: option.value,
        selected_project_label: option.label || null,
        search_query: projectSearchQuery.trim() || null
      });

      hasUserSelectedProjectRef.current = true;
      setFilters(prev => ({
        ...prev,
        projects: option
      }));
      setProjectSearchQuery(option.label || '');
      setIsProjectSearchFocused(false);
      setIsProjectSelectionModalOpen(false);
    },
    [logProjectReportInteraction, projectSearchQuery, selectedProjectUid]
  );

  const handleProjectSearchFocus = useCallback(() => {
    if (!isProjectSearchFocused) {
      logProjectReportInteraction('project_search_focus');
    }
    setIsProjectSearchFocused(true);
  }, [isProjectSearchFocused, logProjectReportInteraction]);

  useEffect(() => {
    if (!isProjectSearchFocused) return;

    const handleOutsideClick = event => {
      if (
        projectSearchRef.current &&
        !projectSearchRef.current.contains(event.target)
      ) {
        setProjectSearchQuery(selectedProjectOption?.label || '');
        setIsProjectSearchFocused(false);
      }
    };

    const handleEscape = event => {
      if (event.key === 'Escape') {
        setProjectSearchQuery(selectedProjectOption?.label || '');
        setIsProjectSearchFocused(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isProjectSearchFocused, selectedProjectOption?.label]);

  useEffect(() => {
    if (isProjectSearchFocused) return;
    setProjectSearchQuery(selectedProjectOption?.label || '');
  }, [isProjectSearchFocused, selectedProjectOption?.label]);

  const filteredProjectSearchOptions = useMemo(() => {
    const query = projectSearchQuery.trim().toLowerCase();
    if (!query) return projectSearchOptions.slice(0, 40);

    return projectSearchOptions
      .filter(option =>
        String(option?.label || '')
          .toLowerCase()
          .includes(query)
      )
      .slice(0, 40);
  }, [projectSearchOptions, projectSearchQuery]);

  useEffect(() => {
    const loadReport = async () => {
      if (
        skipSelectionSyncRef.current &&
        skipSelectionSyncRef.current === selectedProjectUid
      ) {
        skipSelectionSyncRef.current = '';
        return;
      }

      if (!filters?.dateRange || !projectsList.length || !selectedProjectUid) {
        return;
      }

      try {
        setLoading(true);
        setReportError('');
        setSummaryTotals({
          effort: null,
          investment: null
        });

        const response = await sendRequest('/dashboard/report-data', 'POST', {
          reportType: 'project',
          filters,
          period: activePeriod
        });
        const reportMeta = response?.data?.meta || {};
        const reportDatasets = response?.data?.datasets || {};
        const responseAverageDeveloperCost = parseNumericInput(
          reportMeta?.averageDeveloperCost
        );
        const resolvedAverageDeveloperCost =
          roleBasedAverageDeveloperCost > 0
            ? roleBasedAverageDeveloperCost
            : Number.isFinite(responseAverageDeveloperCost)
              ? responseAverageDeveloperCost
              : 0;
        const shouldConvertInvestment =
          filters?.operation === 'investment' &&
          resolvedAverageDeveloperCost > 0;
        const nextRankedProjects = projectsList;
        const resolvedSelectedProjectUid =
          reportMeta?.selectedUid || selectedProjectUid;
        const resolvedSelectedProjectOption = findOptionByValue(
          projectsList,
          resolvedSelectedProjectUid
        );
        const resolvedSelectedProject =
          (resolvedSelectedProjectUid
            ? projectsMap?.[resolvedSelectedProjectUid]
            : null) || resolvedSelectedProjectOption;

        if (
          roleBasedAverageDeveloperCost <= 0 &&
          Number.isFinite(responseAverageDeveloperCost) &&
          responseAverageDeveloperCost > 0
        ) {
          setFallbackAverageDeveloperCost(previousValue =>
            previousValue === responseAverageDeveloperCost
              ? previousValue
              : responseAverageDeveloperCost
          );
        }

        setRankedProjects(nextRankedProjects);

        if (
          resolvedSelectedProjectOption &&
          resolvedSelectedProjectUid &&
          resolvedSelectedProjectUid !== selectedProjectUid
        ) {
          skipSelectionSyncRef.current = resolvedSelectedProjectUid;
          setFilters(prev => ({
            ...prev,
            projects: resolvedSelectedProjectOption
          }));
        }

        const selectedTrendRaw = reportDatasets?.selectedTrend || {};

        if (
          !periodManualOverrideRef.current &&
          !periodAutoResolvedRef.current
        ) {
          const suggestedPeriod = getSuggestedPeriodByDataRecency(
            Object.keys(selectedTrendRaw?.groupedDataByPeriod || {})
          );
          periodAutoResolvedRef.current = true;

          if (suggestedPeriod !== activePeriod) {
            setActivePeriod(suggestedPeriod);
          }
        }

        const selectedEffortTrendRaw =
          reportDatasets?.selectedEffortTrend || {};
        const selectedInvestmentTrendRaw =
          reportDatasets?.selectedInvestmentTrend || {};
        const repoTrendRaw = reportDatasets?.repoTrend || {};
        const categoryTrendRaw = reportDatasets?.categoryTrend || {};
        const authorTrendRaw = reportDatasets?.authorTrend || {};
        const aiTrendRaw = reportDatasets?.aiTrend || {};
        const developerTrendRaw = reportDatasets?.developerTrend || {};
        const retentionData = reportDatasets?.retention || [];

        const selectedTrend = shouldConvertInvestment
          ? convertTrendResponseToInvestment(
              selectedTrendRaw,
              resolvedAverageDeveloperCost
            )
          : selectedTrendRaw;

        const selectedEffortTotalRaw = parseNumericInput(
          selectedEffortTrendRaw?.total?.[resolvedSelectedProjectUid]
        );
        const selectedEffortTotal = Number.isFinite(selectedEffortTotalRaw)
          ? selectedEffortTotalRaw
          : null;
        const investmentFromRate =
          selectedEffortTotal !== null && resolvedAverageDeveloperCost > 0
            ? convertEffortToInvestment(
                selectedEffortTotal,
                resolvedAverageDeveloperCost
              )
            : null;
        const selectedInvestmentTotalRaw = parseNumericInput(
          selectedInvestmentTrendRaw?.total?.[resolvedSelectedProjectUid]
        );
        const selectedInvestmentTotal = Number.isFinite(
          selectedInvestmentTotalRaw
        )
          ? selectedInvestmentTotalRaw
          : null;

        setSummaryTotals({
          effort: selectedEffortTotal,
          investment:
            investmentFromRate !== null
              ? investmentFromRate
              : selectedInvestmentTotal
        });

        const repoTrend = shouldConvertInvestment
          ? convertTrendResponseToInvestment(
              repoTrendRaw,
              resolvedAverageDeveloperCost
            )
          : repoTrendRaw;
        const categoryTrend = shouldConvertInvestment
          ? convertTrendResponseToInvestment(
              categoryTrendRaw,
              resolvedAverageDeveloperCost
            )
          : categoryTrendRaw;
        const authorTrend = shouldConvertInvestment
          ? convertTrendResponseToInvestment(
              authorTrendRaw,
              resolvedAverageDeveloperCost
            )
          : authorTrendRaw;
        const aiTrend = shouldConvertInvestment
          ? convertTrendResponseToInvestment(
              aiTrendRaw,
              resolvedAverageDeveloperCost
            )
          : aiTrendRaw;
        const developerTrend = shouldConvertInvestment
          ? convertTrendResponseToInvestment(
              developerTrendRaw,
              resolvedAverageDeveloperCost
            )
          : developerTrendRaw;

        const selectedMap = {};
        Object.entries(selectedTrend?.groupedDataByPeriod || {}).forEach(
          ([date, values]) => {
            selectedMap[date] = values?.[resolvedSelectedProjectUid] ?? null;
          }
        );

        const selectedSeries = createSeriesFromMap(selectedMap);

        setProjectTrendChart({
          categories: selectedSeries.categories,
          series: [
            {
              name: resolvedSelectedProject?.label || 'Selected project',
              data: selectedSeries.values,
              type: 'spline',
              marker: { enabled: false },
              lineWidth: 3,
              color: '#1D4ED8'
            }
          ]
        });

        const repoKeys = getTopKeysByTotal(repoTrend, 8);
        const repoCategories = Object.keys(
          repoTrend?.groupedDataByPeriod || {}
        );
        const repoSeries = repoKeys
          .map(key => ({
            name: userInfo?.dashboardData?.repos?.[key]?.label || key,
            data: repoCategories.map(date => {
              const value = repoTrend?.groupedDataByPeriod?.[date]?.[key];
              return typeof value === 'number' ? value : null;
            })
          }))
          .filter(item => hasNumericSeriesData(item.data));

        setRepoTrendChart({
          categories: repoCategories,
          series: repoSeries
        });

        const categoryTotals = categoryTrend?.total || {};
        const fallbackCategoryKeys = Object.keys(categoryTotals);
        const configuredCategoryKeys = Object.keys(
          userInfo?.dashboardData?.categories || {}
        );
        const categoryKeys = (
          configuredCategoryKeys.length
            ? configuredCategoryKeys
            : fallbackCategoryKeys
        ).slice(0, 10);

        const categorySeries = categoryKeys
          .map(key => ({
            name: userInfo?.dashboardData?.categories?.[key]?.label || key,
            data: Object.keys(categoryTrend?.groupedDataByPeriod || {}).map(
              date => {
                const value = categoryTrend?.groupedDataByPeriod?.[date]?.[key];
                return typeof value === 'number' ? value : null;
              }
            )
          }))
          .filter(item => hasNumericSeriesData(item.data));

        setCategoryTrendChart({
          categories: Object.keys(categoryTrend?.groupedDataByPeriod || {}),
          series: categorySeries
        });

        const repoPie = buildDistributionFromTotals(
          repoTrend?.total,
          repoUid =>
            userInfo?.dashboardData?.repos?.[repoUid]?.label || repoUid,
          10
        );
        setRepoDistribution(repoPie);

        const developerPie = buildDistributionFromTotals(
          authorTrend?.total,
          authorUid =>
            userInfo?.dashboardData?.authors?.[authorUid]?.label || authorUid,
          10
        );
        setDeveloperDistribution(developerPie);

        const aiPie = buildAiDevDistribution(
          aiTrend?.total,
          developerTrend?.total,
          shouldConvertInvestment
        );
        setAiDistribution(aiPie);

        const categoryPie = buildCategoryDistribution(
          categoryTrend,
          userInfo?.dashboardData?.categories
        );
        setCategoryDistribution(categoryPie);

        setRetention(retentionData?.[0]?.retention || []);
      } catch (error) {
        setReportError(
          error?.message || 'Project report data could not be loaded.'
        );
      } finally {
        setLoading(false);
      }
    };

    loadReport();
  }, [
    activePeriod,
    filters,
    projectsList,
    projectsMap,
    roleBasedAverageDeveloperCost,
    selectedProjectUid,
    selectedProject?.label,
    userInfo?.dashboardData?.authors,
    userInfo?.dashboardData?.categories,
    userInfo?.dashboardData?.repos
  ]);

  useEffect(() => {
    feedHasMoreRef.current = feedHasMore;
  }, [feedHasMore]);

  useEffect(() => {
    feedLoadingRef.current = feedLoading;
  }, [feedLoading]);

  const loadFeed = useCallback(
    async ({ append = false, cursor = null } = {}) => {
      if (!selectedProjectUid || !filters?.dateRange) return;
      if (append && (feedLoadingRef.current || !feedHasMoreRef.current)) return;

      if (append && feedBufferedItemsRef.current.length) {
        const nextChunk = feedBufferedItemsRef.current.slice(
          0,
          FEED_RENDER_PAGE_SIZE
        );
        feedBufferedItemsRef.current = feedBufferedItemsRef.current.slice(
          FEED_RENDER_PAGE_SIZE
        );

        const nextHasMore =
          feedBufferedItemsRef.current.length > 0 ||
          feedBackendHasMoreRef.current;
        setFeedItems(prev => [...prev, ...nextChunk]);
        setFeedHasMore(nextHasMore);
        feedHasMoreRef.current = nextHasMore;
        return;
      }

      const requestId = feedRequestIdRef.current + 1;
      feedRequestIdRef.current = requestId;
      setFeedLoading(true);

      if (!append) setFeedError('');

      try {
        const body = {
          limit: FEED_REQUEST_PAGE_SIZE,
          filters: {
            startDate: filters.dateRange.startDate,
            endDate: filters.dateRange.endDate,
            search: '',
            projects: [selectedProjectUid],
            repos: [],
            developers: [],
            categories: []
          }
        };

        const validCursor = getValidDevFeedCursor(cursor);
        if (append && validCursor) {
          body.cursor = validCursor;
        }

        const response = await sendRequest('/dev-feed', 'POST', body);
        if (requestId !== feedRequestIdRef.current) return;

        const nextItems = response?.data || [];
        const pagination = response?.pagination || {};
        const visibleItems = nextItems.slice(0, FEED_RENDER_PAGE_SIZE);

        feedBufferedItemsRef.current = nextItems.slice(FEED_RENDER_PAGE_SIZE);
        feedBackendHasMoreRef.current = Boolean(pagination?.hasMore);

        setFeedItems(prev =>
          append ? [...prev, ...visibleItems] : [...visibleItems]
        );
        setFeedCursor(pagination?.nextCursor || null);
        const nextHasMore =
          feedBufferedItemsRef.current.length > 0 ||
          feedBackendHasMoreRef.current;
        setFeedHasMore(nextHasMore);
        feedHasMoreRef.current = nextHasMore;
      } catch (error) {
        if (requestId !== feedRequestIdRef.current) return;

        const message = error?.message || 'Failed to load project feed.';
        setFeedError(message);
        if (!append) {
          setFeedItems([]);
          setFeedCursor(null);
          setFeedHasMore(false);
          feedHasMoreRef.current = false;
          feedBufferedItemsRef.current = [];
          feedBackendHasMoreRef.current = false;
        } else if (message.includes('Invalid cursor id')) {
          setFeedHasMore(false);
          feedHasMoreRef.current = false;
        }
      } finally {
        if (requestId === feedRequestIdRef.current) {
          setFeedLoading(false);
        }
      }
    },
    [filters?.dateRange, selectedProjectUid]
  );

  useEffect(() => {
    if (!selectedProjectUid || !filters?.dateRange) return;

    setFeedItems([]);
    setFeedCursor(null);
    setFeedHasMore(true);
    feedHasMoreRef.current = true;
    feedBufferedItemsRef.current = [];
    feedBackendHasMoreRef.current = true;

    loadFeed({ append: false, cursor: null });
  }, [
    selectedProjectUid,
    filters?.dateRange?.startDate,
    filters?.dateRange?.endDate,
    loadFeed
  ]);

  const retentionMetric =
    filters?.calculationsMethod === 'ratio'
      ? 'start_value_ratio'
      : 'start_value';
  const retentionHeatmap = useMemo(() => {
    if (!retention.length) return null;
    return createHeatmapData(retention, retentionMetric, {
      displayMode: filters?.operation || 'git_effort',
      averageDeveloperCost
    });
  }, [
    averageDeveloperCost,
    filters?.operation,
    isDarkMode,
    retention,
    retentionMetric
  ]);
  const resolvedRetentionScreenState = loading
    ? RETENTION_SCREEN_STATES.READY
    : baseRetentionScreenState !== RETENTION_SCREEN_STATES.READY
      ? baseRetentionScreenState
      : retentionHeatmap
        ? RETENTION_SCREEN_STATES.READY
        : RETENTION_SCREEN_STATES.ANALYSIS_PENDING;
  const retentionOverlayContent = getRetentionScreenOverlayContent(
    resolvedRetentionScreenState,
    screenStateMeta
  );

  const metricLabel =
    filters?.operation === 'investment'
      ? 'Estimated Cost Without AI ($)'
      : 'REV';
  const hasReportSnapshot = [
    projectTrendChart?.categories?.length,
    repoTrendChart?.categories?.length,
    categoryTrendChart?.categories?.length,
    developerDistribution.length,
    aiDistribution.length,
    repoDistribution.length,
    categoryDistribution.length,
    retention.length,
    feedItems.length,
    reportError
  ].some(Boolean);

  const isRepoStackedTrendMode = repoTrendViewMode === 'stacked';
  const isCategoryStackedTrendMode = categoryTrendViewMode === 'stacked';

  const repoTrendSeries = useMemo(
    () =>
      (repoTrendChart?.series || []).map(item => ({
        ...item,
        type: isRepoStackedTrendMode ? 'area' : 'spline',
        stack: isRepoStackedTrendMode ? 'repos' : undefined,
        data: (item?.data || []).map(value => {
          if (!isRepoStackedTrendMode) return value;
          return typeof value === 'number' && !Number.isNaN(value) ? value : 0;
        }),
        marker: { enabled: false },
        lineWidth: isRepoStackedTrendMode ? 1.4 : 2,
        fillOpacity: isRepoStackedTrendMode ? 0.72 : 0
      })),
    [isRepoStackedTrendMode, repoTrendChart?.series]
  );

  const categoryTrendSeries = useMemo(
    () =>
      (categoryTrendChart?.series || []).map(item => ({
        ...item,
        type: isCategoryStackedTrendMode ? 'area' : 'spline',
        stack: isCategoryStackedTrendMode ? 'categories' : undefined,
        data: (item?.data || []).map(value => {
          if (!isCategoryStackedTrendMode) return value;
          return typeof value === 'number' && !Number.isNaN(value) ? value : 0;
        }),
        marker: { enabled: false },
        lineWidth: isCategoryStackedTrendMode ? 1.3 : 2,
        fillOpacity: isCategoryStackedTrendMode ? 0.7 : 0
      })),
    [categoryTrendChart?.series, isCategoryStackedTrendMode]
  );

  const handleRepoTrendViewModeChange = useCallback(
    nextMode => {
      logProjectReportInteraction('trend_view_toggle_click', {
        chart_scope: 'repo_trends',
        trend_view_mode: nextMode
      });

      if (nextMode === repoTrendViewMode) return;
      setRepoTrendViewMode(nextMode);
    },
    [logProjectReportInteraction, repoTrendViewMode]
  );

  const handleCategoryTrendViewModeChange = useCallback(
    nextMode => {
      logProjectReportInteraction('trend_view_toggle_click', {
        chart_scope: 'category_trends',
        trend_view_mode: nextMode
      });

      if (nextMode === categoryTrendViewMode) return;
      setCategoryTrendViewMode(nextMode);
    },
    [categoryTrendViewMode, logProjectReportInteraction]
  );

  const createPieOptions = (seriesData, seriesLabel) => {
    const maxPointIndex = seriesData.reduce((bestIndex, item, index, list) => {
      const bestValue = Number(list?.[bestIndex]?.y || 0);
      const currentValue = Number(item?.y || 0);
      return currentValue > bestValue ? index : bestIndex;
    }, 0);

    const total = seriesData.reduce(
      (sum, item) => sum + Number(item?.y || 0),
      0
    );

    const normalizedData = seriesData.map((item, index) => ({
      ...item,
      color: getPieColor(item, index),
      custom: {
        ...item.custom,
        isTopSlice: index === maxPointIndex,
        percentage: total > 0 ? (Number(item?.y || 0) / total) * 100 : 0
      }
    }));

    return {
      credits: { enabled: false },
      legend: { enabled: false },
      chart: {
        height: 340,
        spacingTop: 0,
        spacingBottom: 8
      },
      title: { text: '' },
      tooltip: {
        backgroundColor: isDarkMode ? '#0f172a' : '#ffffff',
        borderColor: isDarkMode ? '#334155' : '#e2e8f0',
        style: {
          color: isDarkMode ? '#f8fafc' : '#0f172a'
        },
        pointFormatter: function () {
          const percent =
            this.point?.custom?.percentage ?? this.percentage ?? 0;
          return `<b>${this.series.name}:</b> ${formatNumber(this.y)} (${formatPiePercentage(percent)})`;
        }
      },
      plotOptions: {
        pie: {
          showInLegend: false,
          size: 230,
          innerSize: '56%',
          dataLabels: {
            enabled: true,
            distance: 20,
            connectorWidth: 1,
            softConnector: true,
            crop: false,
            overflow: 'allow',
            style: {
              color: isDarkMode ? '#e2e8f0' : '#334155',
              textOutline: 'none'
            },
            formatter: function () {
              if (!this.point?.custom?.isTopSlice) return null;
              const percent =
                this.point?.custom?.percentage ?? this.percentage ?? 0;
              return `${this.point.name}: ${formatNumber(this.y)} (${formatPiePercentage(percent)})`;
            }
          }
        }
      },
      responsive: {
        rules: [
          {
            condition: {
              maxWidth: 480
            },
            chartOptions: {
              chart: {
                height: 300
              },
              plotOptions: {
                pie: {
                  size: 190,
                  dataLabels: {
                    distance: 14
                  }
                }
              }
            }
          }
        ]
      },
      series: [
        {
          type: 'pie',
          name: seriesLabel,
          data: normalizedData
        }
      ]
    };
  };

  return (
    <div className="space-y-4 md:space-y-6">
      <div
        className={`z-40 rounded-md border px-3 py-2.5 shadow-default sm:px-4 sm:py-3 md:sticky md:top-20 ${
          isDarkMode
            ? 'border-slate-700 bg-slate-900/95 backdrop-blur'
            : 'border-stroke bg-white'
        }`}
      >
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="mt-1 flex flex-wrap items-center gap-1.5">
              <h2
                className={`text-lg font-semibold ${
                  isDarkMode ? 'text-white' : 'text-slate-900'
                }`}
              >
                {selectedProject?.label || 'Select a project'}
              </h2>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => navigateProject('prev')}
              disabled={!selectedProjectUid}
              className={`rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors ${
                isDarkMode
                  ? 'border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50'
                  : 'border-stroke text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50'
              }`}
            >
              ← Prev
            </button>
            <button
              type="button"
              onClick={() => setIsProjectSelectionModalOpen(true)}
              className={`rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors ${
                isDarkMode
                  ? 'border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800'
                  : 'border-stroke text-slate-700 hover:bg-slate-50'
              }`}
            >
              {selectedProjectUid ? 'Change' : 'Select'}
            </button>
            <button
              type="button"
              onClick={() => navigateProject('next')}
              disabled={!selectedProjectUid}
              className={`rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors ${
                isDarkMode
                  ? 'border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50'
                  : 'border-stroke text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50'
              }`}
            >
              Next →
            </button>
          </div>
        </div>
        <div className="mt-2.5 flex flex-col gap-2 xl:flex-row xl:items-center xl:justify-between">
          <div className="flex flex-wrap items-center gap-2">
            <Filters
              userInfo={userInfo}
              onChange={handleFilters}
              isHidden={[
                'projects',
                'repos',
                'authors',
                'categories',
                'tags',
                'language_tags',
                'viewType',
                'clearButton'
              ]}
              operationKeys={['GIT_EFFORT', 'INVESTMENT']}
              showContextSummary={true}
              summaryCategory="project_report"
            />
            <div
              className="relative w-full sm:w-[280px] xl:w-[260px]"
              ref={projectSearchRef}
            >
              <div
                className={`flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-xs ${
                  isDarkMode
                    ? 'border-slate-700 bg-slate-900 text-slate-200'
                    : 'border-stroke bg-white text-slate-700'
                }`}
              >
                <SearchIcon
                  className={`!h-4 !w-4 ${
                    isDarkMode ? 'text-slate-500' : 'text-slate-400'
                  }`}
                />
                <input
                  ref={projectSearchInputRef}
                  type="text"
                  value={projectSearchQuery}
                  onFocus={handleProjectSearchFocus}
                  onChange={event => {
                    setProjectSearchQuery(event.target.value);
                    setIsProjectSearchFocused(true);
                  }}
                  onKeyDown={event => {
                    if (
                      event.key === 'Enter' &&
                      filteredProjectSearchOptions[0]
                    ) {
                      event.preventDefault();
                      handleProjectSearchSelect(
                        filteredProjectSearchOptions[0]
                      );
                    }
                  }}
                  placeholder="Search project..."
                  className={`w-full bg-transparent text-sm outline-none ${
                    isDarkMode
                      ? 'text-slate-100 placeholder:text-slate-500'
                      : 'text-slate-700 placeholder:text-slate-400'
                  }`}
                />
                {!!projectSearchQuery && (
                  <button
                    type="button"
                    onMouseDown={event => event.preventDefault()}
                    onClick={() => {
                      setProjectSearchQuery('');
                      setIsProjectSearchFocused(true);
                      projectSearchInputRef.current?.focus();
                    }}
                    aria-label="Clear search"
                    className={`rounded-sm px-1 text-base leading-none transition-colors ${
                      isDarkMode
                        ? 'text-slate-500 hover:text-slate-200'
                        : 'text-slate-400 hover:text-slate-700'
                    }`}
                  >
                    ×
                  </button>
                )}
              </div>

              {isProjectSearchFocused && (
                <div
                  className={`absolute left-0 top-full z-50 mt-2 max-h-64 w-full overflow-auto rounded-md border py-1 shadow-lg ${
                    isDarkMode
                      ? 'border-slate-700 bg-slate-900'
                      : 'border-stroke bg-white'
                  }`}
                >
                  {filteredProjectSearchOptions.length ? (
                    filteredProjectSearchOptions.map(option => {
                      const isSelected = option.value === selectedProjectUid;

                      return (
                        <button
                          key={option.value}
                          type="button"
                          onMouseDown={event => event.preventDefault()}
                          onClick={() => handleProjectSearchSelect(option)}
                          className={`flex w-full items-center justify-between px-3 py-2 text-left text-sm transition-colors ${
                            isSelected
                              ? 'bg-primary/10 font-semibold text-primary'
                              : isDarkMode
                                ? 'text-slate-200 hover:bg-slate-800'
                                : 'text-slate-700 hover:bg-slate-50'
                          }`}
                        >
                          <span className="truncate">{option.label}</span>
                          {isSelected && (
                            <span className="ml-2 text-xs font-semibold uppercase tracking-wide">
                              Selected
                            </span>
                          )}
                        </button>
                      );
                    })
                  ) : (
                    <p
                      className={`px-3 py-2 text-sm ${
                        isDarkMode ? 'text-slate-400' : 'text-slate-500'
                      }`}
                    >
                      No projects found.
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
          <SegmentedControl
            items={PERIODS}
            value={activePeriod}
            onChange={handlePeriodChange}
          />
        </div>
      </div>

      {reportError && (
        <div
          className={`rounded-sm border px-4 py-3 text-sm ${
            isDarkMode
              ? 'border-red-500/30 bg-red-950/40 text-red-200'
              : 'border-red-200 bg-red-50 text-red-700'
          }`}
        >
          {reportError}
        </div>
      )}

      {loading && !hasReportSnapshot ? (
        <div
          className={`rounded-sm border p-6 shadow-default ${
            isDarkMode
              ? 'border-slate-700 bg-slate-900'
              : 'border-stroke bg-white'
          }`}
        >
          <Loading type="spline" />
        </div>
      ) : (
        <>
          {loading && (
            <div
              className={`rounded-sm border px-3 py-2 text-xs font-medium ${
                isDarkMode
                  ? 'border-slate-700 bg-slate-800/80 text-slate-300'
                  : 'border-slate-200 bg-slate-50 text-slate-600'
              }`}
            >
              Updating report...
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div
              className={`flex h-full flex-col rounded-sm border p-4 shadow-default ${
                isDarkMode
                  ? 'border-slate-700 bg-slate-900'
                  : 'border-stroke bg-white'
              }`}
            >
              <p
                className={`text-xs font-semibold uppercase tracking-wide ${
                  isDarkMode ? 'text-slate-400' : 'text-slate-500'
                }`}
              >
                Baseline Effort (Without AI)
              </p>
              <p
                className={`mt-1 text-xs ${
                  isDarkMode ? 'text-slate-400' : 'text-slate-500'
                }`}
              >
                R.E.V. shows how much contribution this project represents, in
                terms of the time a typical developer would spend.
              </p>
              <p
                className={`mt-auto pt-3 text-2xl font-bold ${
                  isDarkMode ? 'text-white' : 'text-slate-900'
                }`}
              >
                {summaryTotals.effort !== null
                  ? formatNumber(summaryTotals.effort)
                  : '-'}
              </p>
            </div>

            <div
              className={`flex h-full flex-col rounded-sm border p-4 shadow-default ${
                isDarkMode
                  ? 'border-slate-700 bg-slate-900'
                  : 'border-stroke bg-white'
              }`}
            >
              <p
                className={`text-xs font-semibold uppercase tracking-wide ${
                  isDarkMode ? 'text-slate-400' : 'text-slate-500'
                }`}
              >
                Estimated Cost Without AI
              </p>
              <p
                className={`mt-1 text-xs ${
                  isDarkMode ? 'text-slate-400' : 'text-slate-500'
                }`}
              >
                Estimated Cost Without AI shows the financial equivalent of the
                baseline effort required to deliver the same project output
                without AI, based on REV and your average developer cost per
                hour.
              </p>
              <p
                className={`mt-auto pt-3 text-2xl font-bold ${
                  isDarkMode ? 'text-white' : 'text-slate-900'
                }`}
              >
                {summaryTotals.investment !== null
                  ? `$${formatNumber(summaryTotals.investment)}`
                  : '-'}
              </p>
            </div>
          </div>

          <div
            className={`rounded-sm border p-4 shadow-default ${
              isDarkMode
                ? 'border-slate-700 bg-slate-900'
                : 'border-stroke bg-white'
            }`}
          >
            <h3
              className={`mb-3 text-base font-semibold ${
                isDarkMode ? 'text-white' : 'text-slate-900'
              }`}
            >
              Project Trend
            </h3>
            <HighchartsReact
              highcharts={Highcharts}
              options={buildZoomableChartOptions({
                credits: { enabled: false },
                title: { text: '' },
                legend: { enabled: true },
                tooltip: {
                  shared: true,
                  useHTML: true,
                  formatter: formatSharedLineTooltip
                },
                xAxis: { categories: projectTrendChart?.categories || [] },
                yAxis: { title: { text: metricLabel } },
                series: projectTrendChart?.series || []
              })}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            <div
              className={`rounded-sm border p-4 shadow-default ${
                isDarkMode
                  ? 'border-slate-700 bg-slate-900'
                  : 'border-stroke bg-white'
              }`}
            >
              <div className="mb-3 flex items-center justify-between gap-2">
                <h3
                  className={`text-base font-semibold ${
                    isDarkMode ? 'text-white' : 'text-slate-900'
                  }`}
                >
                  Repo Trends
                </h3>
                <div
                  className={`inline-flex items-center rounded-md border p-0.5 ${
                    isDarkMode
                      ? 'border-slate-700 bg-slate-800/80'
                      : 'border-slate-200 bg-slate-50'
                  }`}
                >
                  <button
                    type="button"
                    title="Line view"
                    aria-label="Line view"
                    onClick={() => handleRepoTrendViewModeChange('line')}
                    className={`rounded-md p-1.5 transition-all ${
                      repoTrendViewMode === 'line'
                        ? isDarkMode
                          ? 'bg-slate-950 text-primary shadow-sm'
                          : 'bg-white text-primary shadow-sm'
                        : isDarkMode
                          ? 'text-slate-400 hover:text-white'
                          : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    <svg
                      viewBox="0 0 20 20"
                      className="h-4 w-4"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="2,14 7,9 11,12 18,5" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    title="100% stacked area view"
                    aria-label="100% stacked area view"
                    onClick={() => handleRepoTrendViewModeChange('stacked')}
                    className={`rounded-md p-1.5 transition-all ${
                      repoTrendViewMode === 'stacked'
                        ? isDarkMode
                          ? 'bg-slate-950 text-primary shadow-sm'
                          : 'bg-white text-primary shadow-sm'
                        : isDarkMode
                          ? 'text-slate-400 hover:text-white'
                          : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    <svg viewBox="0 0 20 20" className="h-4 w-4 fill-current">
                      <path
                        d="M2 16L2 12L8 10L14 11L18 9L18 16Z"
                        opacity="0.35"
                      />
                      <path
                        d="M2 12L2 8L8 6L14 7L18 4L18 9L14 11L8 10Z"
                        opacity="0.65"
                      />
                    </svg>
                  </button>
                </div>
              </div>
              <HighchartsReact
                highcharts={Highcharts}
                options={buildZoomableChartOptions({
                  credits: { enabled: false },
                  title: { text: '' },
                  legend: { enabled: true },
                  tooltip: {
                    shared: true,
                    useHTML: true,
                    formatter: isRepoStackedTrendMode
                      ? formatSharedStackedTooltip
                      : formatSharedLineTooltip
                  },
                  xAxis: { categories: repoTrendChart?.categories || [] },
                  yAxis: {
                    title: {
                      text: isRepoStackedTrendMode ? 'Share (%)' : metricLabel
                    },
                    min: isRepoStackedTrendMode ? 0 : null,
                    max: isRepoStackedTrendMode ? 100 : null,
                    labels: {
                      formatter: function () {
                        return isRepoStackedTrendMode
                          ? `${Number(this.value)}%`
                          : this.value;
                      }
                    }
                  },
                  plotOptions: isRepoStackedTrendMode
                    ? {
                        area: {
                          stacking: 'percent'
                        }
                      }
                    : {},
                  series: repoTrendSeries
                })}
              />
            </div>

            <div
              className={`rounded-sm border p-4 shadow-default ${
                isDarkMode
                  ? 'border-slate-700 bg-slate-900'
                  : 'border-stroke bg-white'
              }`}
            >
              <div className="mb-3 flex items-center justify-between gap-2">
                <h3
                  className={`text-base font-semibold ${
                    isDarkMode ? 'text-white' : 'text-slate-900'
                  }`}
                >
                  Category Trends
                </h3>
                <div
                  className={`inline-flex items-center rounded-md border p-0.5 ${
                    isDarkMode
                      ? 'border-slate-700 bg-slate-800/80'
                      : 'border-slate-200 bg-slate-50'
                  }`}
                >
                  <button
                    type="button"
                    title="Line view"
                    aria-label="Line view"
                    onClick={() => handleCategoryTrendViewModeChange('line')}
                    className={`rounded-md p-1.5 transition-all ${
                      categoryTrendViewMode === 'line'
                        ? isDarkMode
                          ? 'bg-slate-950 text-primary shadow-sm'
                          : 'bg-white text-primary shadow-sm'
                        : isDarkMode
                          ? 'text-slate-400 hover:text-white'
                          : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    <svg
                      viewBox="0 0 20 20"
                      className="h-4 w-4"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="2,14 7,9 11,12 18,5" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    title="100% stacked area view"
                    aria-label="100% stacked area view"
                    onClick={() => handleCategoryTrendViewModeChange('stacked')}
                    className={`rounded-md p-1.5 transition-all ${
                      categoryTrendViewMode === 'stacked'
                        ? isDarkMode
                          ? 'bg-slate-950 text-primary shadow-sm'
                          : 'bg-white text-primary shadow-sm'
                        : isDarkMode
                          ? 'text-slate-400 hover:text-white'
                          : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    <svg viewBox="0 0 20 20" className="h-4 w-4 fill-current">
                      <path
                        d="M2 16L2 12L8 10L14 11L18 9L18 16Z"
                        opacity="0.35"
                      />
                      <path
                        d="M2 12L2 8L8 6L14 7L18 4L18 9L14 11L8 10Z"
                        opacity="0.65"
                      />
                    </svg>
                  </button>
                </div>
              </div>
              <HighchartsReact
                highcharts={Highcharts}
                options={buildZoomableChartOptions({
                  credits: { enabled: false },
                  title: { text: '' },
                  legend: { enabled: true },
                  tooltip: {
                    shared: true,
                    useHTML: true,
                    formatter: isCategoryStackedTrendMode
                      ? formatSharedStackedTooltip
                      : formatSharedLineTooltip
                  },
                  xAxis: { categories: categoryTrendChart?.categories || [] },
                  yAxis: {
                    title: {
                      text: isCategoryStackedTrendMode
                        ? 'Share (%)'
                        : metricLabel
                    },
                    min: isCategoryStackedTrendMode ? 0 : null,
                    max: isCategoryStackedTrendMode ? 100 : null,
                    labels: {
                      formatter: function () {
                        return isCategoryStackedTrendMode
                          ? `${Number(this.value)}%`
                          : this.value;
                      }
                    }
                  },
                  plotOptions: isCategoryStackedTrendMode
                    ? {
                        area: {
                          stacking: 'percent'
                        }
                      }
                    : {},
                  series: categoryTrendSeries
                })}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-4">
            <div
              className={`rounded-sm border p-4 shadow-default xl:col-span-1 ${
                isDarkMode
                  ? 'border-slate-700 bg-slate-900'
                  : 'border-stroke bg-white'
              }`}
            >
              <h3
                className={`mb-3 text-base font-semibold ${
                  isDarkMode ? 'text-white' : 'text-slate-900'
                }`}
              >
                Developer Distribution
              </h3>
              {developerDistribution.length ? (
                <>
                  <HighchartsReact
                    highcharts={Highcharts}
                    options={createPieOptions(
                      developerDistribution,
                      selectedProject?.label || 'Selected project'
                    )}
                  />
                  {renderPieLegend(developerDistribution, isDarkMode)}
                </>
              ) : (
                <p
                  className={`text-sm ${
                    isDarkMode ? 'text-slate-400' : 'text-slate-500'
                  }`}
                >
                  No developer distribution data in selected range.
                </p>
              )}
            </div>

            <div
              className={`rounded-sm border p-4 shadow-default xl:col-span-1 ${
                isDarkMode
                  ? 'border-slate-700 bg-slate-900'
                  : 'border-stroke bg-white'
              }`}
            >
              <h3
                className={`mb-3 text-base font-semibold ${
                  isDarkMode ? 'text-white' : 'text-slate-900'
                }`}
              >
                AI / Developer Distribution
              </h3>
              {aiDistribution.length ? (
                <>
                  <HighchartsReact
                    highcharts={Highcharts}
                    options={createPieOptions(
                      aiDistribution,
                      selectedProject?.label || 'Selected project'
                    )}
                  />
                  {renderPieLegend(aiDistribution, isDarkMode)}
                </>
              ) : (
                <p
                  className={`text-sm ${
                    isDarkMode ? 'text-slate-400' : 'text-slate-500'
                  }`}
                >
                  No AI/Developer distribution data in selected range.
                </p>
              )}
            </div>

            <div
              className={`rounded-sm border p-4 shadow-default xl:col-span-1 ${
                isDarkMode
                  ? 'border-slate-700 bg-slate-900'
                  : 'border-stroke bg-white'
              }`}
            >
              <h3
                className={`mb-3 text-base font-semibold ${
                  isDarkMode ? 'text-white' : 'text-slate-900'
                }`}
              >
                Repo Distribution
              </h3>
              {repoDistribution.length ? (
                <>
                  <HighchartsReact
                    highcharts={Highcharts}
                    options={createPieOptions(
                      repoDistribution,
                      selectedProject?.label || 'Selected project'
                    )}
                  />
                  {renderPieLegend(repoDistribution, isDarkMode)}
                </>
              ) : (
                <p
                  className={`text-sm ${
                    isDarkMode ? 'text-slate-400' : 'text-slate-500'
                  }`}
                >
                  No repo distribution data in selected range.
                </p>
              )}
            </div>

            <div
              className={`rounded-sm border p-4 shadow-default xl:col-span-1 ${
                isDarkMode
                  ? 'border-slate-700 bg-slate-900'
                  : 'border-stroke bg-white'
              }`}
            >
              <h3
                className={`mb-3 text-base font-semibold ${
                  isDarkMode ? 'text-white' : 'text-slate-900'
                }`}
              >
                Category Distribution
              </h3>
              {categoryDistribution.length ? (
                <>
                  <HighchartsReact
                    highcharts={Highcharts}
                    options={createPieOptions(
                      categoryDistribution,
                      selectedProject?.label || 'Selected project'
                    )}
                  />
                  {renderPieLegend(categoryDistribution, isDarkMode)}
                </>
              ) : (
                <p
                  className={`text-sm ${
                    isDarkMode ? 'text-slate-400' : 'text-slate-500'
                  }`}
                >
                  No category distribution data in selected range.
                </p>
              )}
            </div>
          </div>

          <div
            className={`rounded-sm border p-4 shadow-default ${
              isDarkMode
                ? 'border-slate-700 bg-slate-900'
                : 'border-stroke bg-white'
            }`}
          >
            <h3
              className={`mb-1 text-base font-semibold ${
                isDarkMode ? 'text-white' : 'text-slate-900'
              }`}
            >
              Project Retention
            </h3>
            <p
              className={`mb-3 text-xs ${
                isDarkMode ? 'text-slate-400' : 'text-slate-500'
              }`}
            >
              Percentage of past engineering effort that remains effective over
              time for this project.
            </p>
            {loading && !retentionHeatmap ? (
              <Loading type="retention" />
            ) : retentionOverlayContent ? (
              <ScreenStatePanel {...retentionOverlayContent} />
            ) : retentionHeatmap ? (
              <HighchartsReact
                highcharts={Highcharts}
                options={buildZoomableChartOptions(
                  {
                    chart: { type: 'heatmap', height: 560 },
                    credits: { enabled: false },
                    title: { text: '' },
                    xAxis: {
                      categories: retentionHeatmap.xCategories,
                      opposite: true
                    },
                    yAxis: {
                      categories: retentionHeatmap.yCategories,
                      title: null,
                      reversed: true
                    },
                    colorAxis: {
                      min: 0,
                      max: 100,
                      stops: getRetentionHeatmapStops(isDarkMode)
                    },
                    series: [
                      {
                        type: 'heatmap',
                        data: retentionHeatmap.data,
                        borderWidth: 1,
                        dataLabels: {
                          enabled: true,
                          useHTML: true,
                          style: {
                            textOutline: 'none'
                          },
                          formatter: function () {
                            const roundedValue = Math.round(this.point.value);
                            const formattedValue = formatNumber(roundedValue);
                            const labelColor = getHeatmapDataLabelColor({
                              value: this.point.value,
                              isDarkMode,
                              isCohortValue: this.point.isCohortValue
                            });

                            if (this.point.isCohortValue) {
                              const label = retentionHeatmap.isInvestment
                                ? `$${formattedValue}`
                                : `${formattedValue}`;

                              return `<span style="color:${labelColor};">${label}</span>`;
                            }

                            return `<span style="color:${labelColor};">${roundedValue}%</span>`;
                          }
                        }
                      }
                    ]
                  },
                  'xy'
                )}
              />
            ) : null}
          </div>

          <div
            className={`rounded-sm border p-4 shadow-default ${
              isDarkMode
                ? 'border-slate-700 bg-slate-900'
                : 'border-stroke bg-white'
            }`}
          >
            <h3
              className={`mb-3 text-base font-semibold ${
                isDarkMode ? 'text-white' : 'text-slate-900'
              }`}
            >
              Project Feed Records
            </h3>
            {feedError && (
              <p className="mb-3 text-sm text-red-500">{feedError}</p>
            )}
            <div
              className={`max-h-[760px] space-y-3 overflow-y-auto rounded-md border p-3 pr-2 ${
                isDarkMode
                  ? 'border-slate-700 bg-slate-950/70'
                  : 'border-stroke bg-slate-50'
              }`}
            >
              {feedItems.map(item => {
                const stats = item?.stats || {};
                const hashtags = item?.hashtags || {};
                const commitSha = item?.commit_sha || '';
                const shortCommitSha = commitSha ? commitSha.slice(0, 6) : '';
                const commitUrl = item?.commit_url || '';
                const value =
                  averageDeveloperCost > 0
                    ? convertEffortToInvestment(
                        stats?.rev,
                        averageDeveloperCost
                      )
                    : typeof stats?.value === 'number'
                      ? Math.round(stats.value)
                      : 0;

                return (
                  <article
                    key={item?.id || `${item?.timestamp}-${item?.author?.name}`}
                    className={`rounded-md border p-4 shadow-sm transition hover:-translate-y-[1px] hover:shadow-md ${
                      isDarkMode
                        ? 'border-slate-700 bg-slate-900'
                        : 'border-stroke bg-white'
                    }`}
                  >
                    <div
                      className={`flex flex-wrap items-center justify-between gap-2 text-sm ${
                        isDarkMode ? 'text-slate-400' : 'text-slate-500'
                      }`}
                    >
                      <div
                        className={`font-semibold ${
                          isDarkMode ? 'text-slate-200' : 'text-slate-700'
                        }`}
                      >
                        {item?.author?.name || 'Unknown Developer'}
                      </div>
                      <div>
                        {item?.timestamp
                          ? formatDistanceToNow(new Date(item.timestamp), {
                              addSuffix: true
                            })
                          : '-'}
                      </div>
                    </div>

                    <div className="mt-3 flex items-start justify-between gap-3">
                      <h4
                        className={`text-sm font-semibold ${
                          isDarkMode ? 'text-white' : 'text-slate-900'
                        }`}
                      >
                        {item?.impact_summary?.title || 'Untitled impact'}
                      </h4>
                      {shortCommitSha && commitUrl && (
                        <a
                          href={commitUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`shrink-0 rounded px-2 py-0.5 font-mono text-xs font-semibold transition ${
                            isDarkMode
                              ? 'bg-slate-800 text-slate-200 hover:bg-slate-700'
                              : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                          }`}
                        >
                          {shortCommitSha}
                        </a>
                      )}
                    </div>
                    <p
                      className={`mt-1 text-sm ${
                        isDarkMode ? 'text-slate-300' : 'text-slate-600'
                      }`}
                    >
                      {item?.impact_summary?.detail || '-'}
                    </p>

                    <div className="mt-3 flex flex-wrap gap-2 text-xs font-medium">
                      {hashtags?.project && (
                        <span
                          className={`rounded-full px-3 py-1 ${
                            isDarkMode
                              ? 'bg-blue-500/15 text-blue-200'
                              : 'bg-blue-50 text-blue-700'
                          }`}
                        >
                          #{hashtags.project_name || hashtags.project}
                        </span>
                      )}
                      {hashtags?.repo && (
                        <span
                          className={`rounded-full px-3 py-1 ${
                            isDarkMode
                              ? 'bg-fuchsia-500/15 text-fuchsia-200'
                              : 'bg-purple-50 text-purple-700'
                          }`}
                        >
                          #{hashtags.repo_name || hashtags.repo}
                        </span>
                      )}
                      {(hashtags?.categories || []).map(category => (
                        <span
                          key={`${item?.id || item?.timestamp}-${category}`}
                          className={`rounded-full px-3 py-1 ${
                            isDarkMode
                              ? 'bg-emerald-500/15 text-emerald-200'
                              : 'bg-emerald-50 text-emerald-700'
                          }`}
                        >
                          #{category}
                        </span>
                      ))}
                    </div>

                    <div
                      className={`mt-4 border-t border-dashed pt-3 text-sm ${
                        isDarkMode
                          ? 'border-slate-700 text-slate-300'
                          : 'border-stroke text-slate-600'
                      }`}
                    >
                      <span className="font-semibold">REV:</span>{' '}
                      {stats?.rev || 0}
                      <span className="mx-2">|</span>
                      <span className="font-semibold">
                        +{stats?.insertions || 0}
                      </span>{' '}
                      / -{stats?.deletions || 0} lines
                      <span className="mx-2">|</span>
                      <span className="font-semibold">Value:</span> ${value}
                    </div>
                  </article>
                );
              })}

              {!feedItems.length && !feedLoading && !feedError && (
                <p
                  className={`text-sm ${
                    isDarkMode ? 'text-slate-400' : 'text-slate-500'
                  }`}
                >
                  No project feed records in selected range.
                </p>
              )}

              {feedLoading && !feedItems.length && (
                <div
                  className={`rounded-md border px-3 py-2 text-sm ${
                    isDarkMode
                      ? 'border-slate-700 bg-slate-800/80 text-slate-300'
                      : 'border-stroke bg-slate-50 text-slate-500'
                  }`}
                >
                  <Loading type="bar" />
                </div>
              )}

              {feedHasMore && feedItems.length > 0 && (
                <div className="flex justify-center px-2 py-2">
                  <button
                    type="button"
                    onClick={() => {
                      logProjectReportInteraction(
                        'project_feed_load_more_click',
                        {
                          has_cursor: Boolean(feedCursor),
                          has_buffered_items:
                            feedBufferedItemsRef.current.length > 0
                        }
                      );
                      loadFeed({ append: true, cursor: feedCursor });
                    }}
                    disabled={feedLoading}
                    className={`rounded-md border px-4 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                      isDarkMode
                        ? 'border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800'
                        : 'border-stroke bg-white text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    {feedLoading ? <Loading type="inline" /> : 'Load more'}
                  </button>
                </div>
              )}

              {!feedHasMore && feedItems.length > 0 && (
                <div
                  className={`px-2 py-2 text-center text-xs ${
                    isDarkMode ? 'text-slate-500' : 'text-slate-400'
                  }`}
                >
                  You&apos;ve reached the end of the feed.
                </div>
              )}
            </div>
          </div>
        </>
      )}
      <EntitySelectionModal
        isOpen={isProjectSelectionModalOpen}
        title="Select a project"
        options={projectsList}
        selectedValue={selectedProjectUid}
        selectedLabel={selectedProject?.label || ''}
        searchPlaceholder="Search project..."
        emptyMessage="No projects found."
        confirmLabel="Pick a project to open the report."
        isDarkMode={isDarkMode}
        canClose={Boolean(selectedProjectUid)}
        onClose={() => setIsProjectSelectionModalOpen(false)}
        onSelect={handleProjectSearchSelect}
      />
    </div>
  );
};

export default ProjectReport;
